import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { fetchClinicBrandsWithLocations } from 'src/supportPortal/services/clinicWorkforceService'
import { supportGoogleBusinessServices } from 'src/supportPortal/services/supportGoogleBusinessService'
import GoogleBusinessSettings from 'src/shared/googleBusiness/GoogleBusinessSettings'
import IntegrationsHub from 'src/shared/integrations/IntegrationsHub'

const BASE_PATH = '/support-home/clinics-hospitals/clinics'

function readStoredBrand() {
  try {
    return JSON.parse(sessionStorage.getItem('supportClinicBrandData') || 'null')
  } catch (e) {
    return null
  }
}

function brandSortValue(brand) {
  return (brand?.title || brand?.brandName || brand?.slug || '').toLowerCase()
}

function brandLabel(brand) {
  return brand?.title || brand?.brandName || brand?.slug || ''
}

// Support-side "Integrations" hub for a clinic brand, reached from the Integrations button on the
// clinics page brand cards. Extensible tile grid; today it holds a single tile (Google Business),
// whose paste-only settings form is the shared GoogleBusinessSettings component (support can't run
// the clinic's Google OAuth consent, so showConnectFlow={false}). Brand is resolved from the
// :brandSlug route param with a sessionStorage fast path — same pattern as the communication
// policy page.
export default function SupportClinicIntegrationsPage() {
  const navigate = useNavigate()
  const { brandSlug } = useParams()
  const [brands, setBrands] = useState([])
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [loadingBrands, setLoadingBrands] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    async function loadBrands() {
      setLoadingBrands(true)
      setError('')
      try {
        const nextBrands = await fetchClinicBrandsWithLocations()
        if (!active) return
        const sortedBrands = [...(nextBrands || [])].sort((a, b) =>
          brandSortValue(a).localeCompare(brandSortValue(b)),
        )
        setBrands(sortedBrands)
        const storedBrand = readStoredBrand()
        const nextSelected =
          sortedBrands.find((brand) => brand?.slug === brandSlug) ||
          sortedBrands.find((brand) => brand?.id === storedBrand?.id) ||
          sortedBrands[0] ||
          null
        setSelectedBrand(nextSelected)
      } catch (loadError) {
        if (active) setError(loadError?.message || 'Unable to load clinic brands')
      } finally {
        if (active) setLoadingBrands(false)
      }
    }
    loadBrands()
    return () => {
      active = false
    }
  }, [brandSlug])

  const handleBrandChange = useCallback(
    (event, brand) => {
      setSelectedBrand(brand || null)
      if (brand) {
        sessionStorage.setItem('supportClinicBrandData', JSON.stringify(brand))
        if (brand.slug && brand.slug !== brandSlug) {
          navigate(`${BASE_PATH}/${brand.slug}/integrations`, { replace: true })
        }
      }
    },
    [navigate, brandSlug],
  )

  const integrations = useMemo(() => {
    if (!selectedBrand?.id) return []
    return [
      {
        id: 'google-business',
        title: 'Google Business',
        description:
          'Set the Google review link patients receive from the "Send Google Review Link" action.',
        Icon: RateReviewOutlinedIcon,
        renderPanel: () => (
          <GoogleBusinessSettings
            clinicBrandId={selectedBrand.id}
            services={supportGoogleBusinessServices}
            showConnectFlow={false}
          />
        ),
      },
    ]
  }, [selectedBrand?.id])

  return (
    <>
      <SupportHeader heading="Integrations" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  size="small"
                  loading={loadingBrands}
                  options={brands}
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  getOptionLabel={brandLabel}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Clinic brand"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingBrands ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Typography variant="body2" color="text.secondary">
                  Connect this clinic brand to the tools its practitioners use with patients.
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {error && <Alert severity="error">{error}</Alert>}

          {loadingBrands && !selectedBrand ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : selectedBrand?.id ? (
            <Paper variant="outlined" sx={{ borderRadius: 1 }}>
              <IntegrationsHub
                integrations={integrations}
                subtitle={`Integrations for ${brandLabel(selectedBrand)}.`}
              />
            </Paper>
          ) : (
            <Typography sx={{ p: 3 }} color="text.secondary">
              Select a clinic brand to manage its integrations.
            </Typography>
          )}
        </Stack>
      </Box>
    </>
  )
}
